<template>
  <div
    data-component="base-item-actions-popup"
    :class="{ [`is-${size}`]: true }"
  >
    <div
      v-if="actions.length"
      data-button="toggle-popup"
      :class="popupButtonState"
      @click="onOpenClick"
    >
      <or-icon icon="more_vert" />

      <div
        v-if="isOpen"
        class="actions"
      >
        <div
          v-for="action of actions"
          :key="action.key"
          :data-button="action.key"
          @click="onActionClick(action.key)"
        >
          {{ action.label }}
        </div>
      </div>
    </div>

    <div
      v-if="description"
      class="description"
    >
      {{ description }}
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue-demi';
import { OrIconV3 as OrIcon } from '@onereach/ui-components-25';
import { ITEM_ACTIONS_PROP, SIZE_PROP } from '../../constants';

export default defineComponent({
  name: 'BaseItemActionsPopup',
  components: { OrIcon },
  props: {
    description: {
      type: String,
      default: '',
    },
    actions: ITEM_ACTIONS_PROP,
    size: SIZE_PROP,
    isDisabled: Boolean,
  },
  emits: ['action-click'],
  data() {
    return {
      isOpen: false,
    };
  },
  computed: {
    popupButtonState() {
      return {
        [`is-${this.size}`]: true,
        'is-disabled': this.isDisabled,
      };
    },
  },
  methods: {
    onOpenClick() {
      if (!this.isDisabled) {
        this.isOpen = !this.isOpen;

        if (this.isOpen) {
          /* Wait for popup to open */
          setTimeout(() => {
            document.addEventListener('click', this.onClosePopup);
          });
        }
      }
    },
    onActionClick(key: string) {
      if (!this.isDisabled) {
        this.$emit('action-click', key);
      }
    },
    onClosePopup() {
      this.isOpen = false;
      document.removeEventListener('click', this.onClosePopup);
    },
  },
});
</script>

<style lang="scss" scoped>
@use '@/styles' as *;
@import '../../styles';

[data-component='base-item-actions-popup'] {
  --option-font-size: var(--fs-1);
  --option-padding: var(--s-3) var(--s-4);
  --option-top-margin: 1.75rem;
  --description-font-size: var(--fs-0);
  --description-top-margin: 0;
  --actions-popup-z-index: 1;

  display: inline-flex;
  flex-direction: column;

  [data-button='toggle-popup'] {
    @include base-icon-button;
    position: relative;
    margin-left: auto;
  }

  &.is-mobile {
    --option-font-size: 1rem;
    --option-padding: 0.75rem 1.5rem;
    --option-top-margin: 2.25rem;
    --description-font-size: var(--fs-1);
    --description-top-margin: var(--s-2);
  }

  .actions {
    @include base-popup;

    display: flex;
    flex-direction: column;
    position: absolute;
    top: var(--option-top-margin);
    right: 0;
    max-width: 15rem;
    z-index: var(--actions-popup-z-index);

    [data-button] {
      @include text-overflow;

      padding: var(--option-padding);
      font-size: var(--option-font-size);

      &:active,
      &:hover {
        background: var(--or-c-primary-opacity-0-08);
      }
    }
  }

  .description {
    @include text-overflow;

    font-size: var(--description-font-size);
    line-height: 1.5;
    margin-top: var(--description-top-margin);
    color: var(--or-c-outline);
    text-align: right;
  }
}
</style>
